import React from 'react'
import { CheckCircleIcon } from '../../lib/components'

const PASSWORD_REQUIREMENTS = [
  { id: 'lowercase', test: (p: string) => /[a-z]/.test(p), label: 'At least 1 lowercase alphabet' },
  { id: 'uppercase', test: (p: string) => /[A-Z]/.test(p), label: 'At least 1 uppercase alphabet' },
  { id: 'number', test: (p: string) => /\d/.test(p), label: 'At least 1 number' },
  { id: 'length', test: (p: string) => p.length >= 8, label: 'At least 8 characters' },
  {
    id: 'special',
    test: (p: string) => /[@$!%*?&]/.test(p),
    label: 'At least 1 special character (@$!%*?&)',
  },
] as const

const STRENGTH_LABELS = ['Weak', 'Fair', 'Good', 'Strong'] as const

function getStrengthLevel(password: string): {
  level: number
  label: (typeof STRENGTH_LABELS)[number]
} {
  if (!password.length) {
    return { level: 0, label: 'Weak' }
  }
  const met = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length
  if (met <= 2) return { level: met, label: 'Weak' }
  if (met === 3) return { level: met, label: 'Fair' }
  if (met === 4) return { level: met, label: 'Good' }
  return { level: 5, label: 'Strong' }
}

type PasswordStrengthIndicatorProps = {
  password: string
}

const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const { level, label } = getStrengthLevel(password)
  const fillPercent = password.length === 0 ? 0 : (level / 5) * 100

  return (
    <div className='sign-up-page__password-strength' data-testid='password-strength-indicator'>
      {password.length > 0 && (
        <>
          <div className='sign-up-page__password-strength-bar-wrap'>
            <div
              className='sign-up-page__password-strength-bar'
              role='progressbar'
              aria-valuenow={Math.round(fillPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Password strength: ${label}`}
            >
              <div
                className='sign-up-page__password-strength-bar-fill'
                style={{ width: `${fillPercent}%` }}
              />
            </div>
            <span className='sign-up-page__password-strength-label'>{label}</span>
          </div>
          <ul className='sign-up-page__password-requirements' aria-label='Password requirements'>
            {PASSWORD_REQUIREMENTS.map(({ id, test, label: reqLabel }) => {
              const met = test(password)
              return (
                <li key={id} className='sign-up-page__password-requirement'>
                  {met ? (
                    <CheckCircleIcon
                      type='progress'
                      width='16px'
                      height='16px'
                      dataTestId={`password-req-${id}-met`}
                    />
                  ) : (
                    <span
                      className='sign-up-page__password-requirement-dot'
                      aria-hidden
                      data-testid={`password-req-${id}-unmet`}
                    />
                  )}
                  <span className={met ? 'sign-up-page__password-requirement-met' : ''}>
                    {reqLabel}
                  </span>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}

export { PasswordStrengthIndicator, PASSWORD_REQUIREMENTS, getStrengthLevel }
